import React, { useCallback, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  BookOpen, CalendarDays, ClipboardList, Layers, Pencil, Play, SearchX, ShieldOff, Square, UserCheck,
  UserPlus, Users, Video,
} from "lucide-react";
import {
  AppButton, Callout, EmptyState, PageHeader, StatsCard, StatsGrid, StatusBadge, Tabs, TabsContent,
  TabsList, TabsTrigger,
} from "../../ui";
import Shimmer from "../../Common/Shimmer";
import CommonToaster from "../../Common/CommonToaster";
import BatchVideosPage from "../../Videos/BatchVideosPage";
import BatchMaterialsPage from "../../Materials/BatchMaterialsPage";
import BatchGeneralAssignments from "../../Assignments/general/BatchGeneralAssignments";
import BatchEnrollmentRequests from "../../Enrollment/BatchEnrollmentRequests";
import BatchSyllabusSchedule from "../../Syllabus/schedule/BatchSyllabusSchedule";
import apiClient from "../../../utils/apiClient";
import { getCurrentUser } from "../../../utils/auth";
import { hasAnyPermission, hasPermission } from "../../../utils/permissions";
import { usePlan } from "../../../contexts/PlanContext";
import useAudienceTerm from "../../../hooks/useAudienceTerm";
import useBatchDetails from "./useBatchDetails";
import BatchIdentityCard from "./BatchIdentityCard";
import BatchVitals from "./BatchVitals";
import { BatchFactsCard, BatchTeamCard } from "./BatchFactsCard";
import BatchStudentsTab from "./BatchStudentsTab";
import BatchAttendanceTab from "./BatchAttendanceTab";
import { ContentExpiryModal, EndBatchModal } from "./BatchLifecycleModals";
import { batchInstructors, lifecycleStep, needsSyllabusSync, offerZoomMeeting } from "./batchDetails";

const TAB_VALUES = ["students", "syllabus", "videos", "materials", "attendance", "assignments", "requests"];

/** A sync answered with one of these is a statement of fact, not a failure — and the prompt should stop. */

/** The skeleton mirrors the page, so nothing jumps when the batch lands. */
const PageSkeleton = () => (
  <div className="tw-flex tw-flex-col tw-gap-4" aria-busy="true" aria-label="Loading batch">
    <Shimmer height={112} />
    <StatsGrid cols={5}>
      {[0, 1, 2, 3, 4].map((i) => <StatsCard key={i} loading />)}
    </StatsGrid>
    <div className="tw-grid tw-gap-4 min-[1200px]:tw-grid-cols-3">
      <div className="min-[1200px]:tw-col-span-2"><Shimmer height={180} /></div>
      <Shimmer height={180} />
    </div>
    <Shimmer height={40} />
  </div>
);

/**
 * BatchDetailsPage — `/batches/:id`, the admin's view of one batch and the
 * place they run it from: start it, end it, set when its content expires, and
 * manage who is in it.
 *
 * Reading order: which batch and its actions; what needs attention (only when
 * something does); five vitals; when and where it meets and who teaches it;
 * then a tab per job. The tab is in the URL (`?tab=attendance`), so a link can
 * land on it and a refresh keeps it.
 *
 * Permissions and plan features are decided HERE and nowhere below, against
 * the same keys the API checks: `manage_batches` edits, ends and expires a
 * batch and creates its Zoom meeting; `manage_syllabus` starts it and syncs its
 * syllabus; `manage_enrollments` adds, transfers and removes students — removing
 * only until the batch ends, when its roster becomes the record.
 */
const BatchDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const terms = useAudienceTerm();
  const { hasPlanFeature } = usePlan();
  const user = getCurrentUser();

  const can = {
    editBatch: hasPermission("manage_batches"),
    runSyllabus: hasPermission("manage_syllabus"),
    reschedule: hasAnyPermission("manage_syllabus", "manage_batches"),
    enrol: hasPermission("manage_enrollments"),
  };
  const showFees = hasPlanFeature("feeManagement") && hasPermission("view_fees");
  const registerOn = hasPlanFeature("attendanceManagement");

  const { batch, stats, assignmentCount, hasSyllabus, pendingRequests, zoom } = useBatchDetails(id, {
    readZoomSettings: can.editBatch,
  });

  // Overrides for what a child or an action learned after the first read.
  const [syncedOverride, setSyncedOverride] = useState(null);
  const [requestCount, setRequestCount] = useState(null);

  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const notify = useCallback((message, type = "success") => setToast({ show: true, message, type }), []);
  const [busy, setBusy] = useState("");
  const [endOpen, setEndOpen] = useState(false);
  const [expiryOpen, setExpiryOpen] = useState(false);

  const requestedTab = searchParams.get("tab");
  const tab = TAB_VALUES.includes(requestedTab) ? requestedTab : "students";
  const setTab = useCallback((next) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === "students") params.delete("tab");
      else params.set("tab", next);
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/batches"));
  const pageHeader = <PageHeader title="Batch details" icon={Layers} backButton onBack={goBack} />;
  const data = batch.data;

  if (batch.loading && !data) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {pageHeader}
        <PageSkeleton />
      </div>
    );
  }

  if (!data) {
    const notFound = batch.error?.response?.status === 404 || (!batch.error && !batch.loading);
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {pageHeader}
        {notFound ? (
          <EmptyState
            icon={SearchX}
            title="Batch not found"
            description="It may have been deleted, or the link belongs to another institute."
            action={<AppButton variant="outline" onClick={() => navigate("/batches")}>Back to batches</AppButton>}
          />
        ) : (
          <Callout
            tone="danger"
            role="alert"
            action={<AppButton size="xs" variant="outline" onClick={batch.reload}>Try again</AppButton>}
          >
            {batch.error?.response?.data?.message || "Could not load this batch."}
          </Callout>
        )}
      </div>
    );
  }

  /* ── Derived ─────────────────────────────────────────────────────────────── */

  const instructors = batchInstructors(data);
  const step = lifecycleStep(data);
  const synced = syncedOverride ?? (hasSyllabus.loading ? null : hasSyllabus.data);
  const pending = requestCount ?? pendingRequests.data ?? 0;
  const courseId = data.course?._id || data.course;
  const role = user?.usertype || "admin";
  // The server refuses both; a college class is filled from Academics.
  const canRemove = can.enrol && data.status !== "Completed" && data.origin !== "academic";
  const currentUserId = user?._id || user?.id;

  /* ── Actions ─────────────────────────────────────────────────────────────── */

  const failure = (err, fallback) => err?.response?.data?.message || fallback;

  const startBatch = async () => {
    setBusy("start");
    try {
      const { data: res } = await apiClient.post(`/syllabus/batch/${id}/start-batch`);
      notify(res.message || "Batch started.");
      batch.reload();
      hasSyllabus.reload();
      setSyncedOverride(null);
    } catch (err) {
      notify(failure(err, "Could not start the batch."), "error");
    }
    setBusy("");
  };

  const endBatch = async (expiryDate) => {
    setBusy("end");
    try {
      const { data: res } = await apiClient.post(`/batches/${id}/end-batch`);
      let message = res.message || "Batch completed.";
      let type = "success";
      if (expiryDate) {
        try {
          await apiClient.put(`/batches/${id}`, { expiryDate });
        } catch (err) {
          message = `The batch was completed, but its content expiry was not saved: ${failure(err, "try again from Set content expiry.")}`;
          type = "warning";
        }
      }
      notify(message, type);
      setEndOpen(false);
      batch.reload();
      stats.reload();
    } catch (err) {
      notify(failure(err, "Could not end the batch."), "error");
    }
    setBusy("");
  };

  const saveExpiry = async (date) => {
    setBusy("expiry");
    try {
      await apiClient.put(`/batches/${id}`, { expiryDate: date || null });
      notify(date ? "Content expiry date saved." : "Content expiry removed.");
      setExpiryOpen(false);
      batch.reload();
    } catch (err) {
      notify(failure(err, "Could not save the content expiry date."), "error");
    }
    setBusy("");
  };

  const createZoomMeeting = async () => {
    setBusy("zoom");
    try {
      const { data: res } = await apiClient.post(`/batches/${id}/create-zoom-meeting`);
      notify(res.message || "Zoom meeting created.");
      batch.reload();
    } catch (err) {
      notify(failure(err, "Could not create the Zoom meeting."), "error");
    }
    setBusy("");
  };

  const refreshCounts = () => {
    stats.reload();
    batch.reload();
  };

  /* ── What to show ────────────────────────────────────────────────────────── */

  const showSyncPrompt = can.runSyllabus && needsSyllabusSync(data, synced);
  const showZoomPrompt = can.editBatch && hasPlanFeature("zoomV2") && offerZoomMeeting(data, zoom.data);

  const headerActions = (
    <>
      {step === "start" && can.runSyllabus && (
        <AppButton size="sm" onClick={startBatch} disabled={busy === "start"}>
          <Play size={16} /> {busy === "start" ? "Starting…" : "Start batch"}
        </AppButton>
      )}
      {step === "end" && can.editBatch && (
        <AppButton size="sm" variant="outline-destructive" onClick={() => setEndOpen(true)} disabled={busy === "end"}>
          <Square size={16} /> End batch
        </AppButton>
      )}
      {step === "expiry" && can.editBatch && (
        <AppButton size="sm" variant="outline" onClick={() => setExpiryOpen(true)}>
          <ShieldOff size={16} /> {data.expiryDate ? "Change content expiry" : "Set content expiry"}
        </AppButton>
      )}
      {can.editBatch && (
        <AppButton size="sm" variant="outline" onClick={() => navigate(`/batches/edit/${id}`)}>
          <Pencil size={16} /> Edit batch
        </AppButton>
      )}
    </>
  );

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {pageHeader}

      <BatchIdentityCard batch={data} instructors={instructors} actions={headerActions} />

      {(showSyncPrompt || showZoomPrompt) && (
        <div className="tw-flex tw-flex-col tw-gap-2">
          {showSyncPrompt && (
            <Callout
              tone="warning"
              action={(
                <AppButton size="xs" variant="outline" onClick={() => setTab("syllabus")}>
                  Plan its classes
                </AppButton>
              )}
            >
              This batch has no class plan yet, so its students see no per-class progress.
              Plan it from the course syllabus on the Syllabus tab.
            </Callout>
          )}
          {showZoomPrompt && (
            <Callout
              tone="warning"
              action={(
                <AppButton size="xs" variant="outline" onClick={createZoomMeeting} disabled={busy === "zoom"}>
                  {busy === "zoom" ? "Creating…" : "Create Zoom meeting"}
                </AppButton>
              )}
            >
              No Zoom meeting is linked to this batch yet. Create one to run its classes online.
            </Callout>
          )}
        </div>
      )}

      <BatchVitals batch={data} stats={stats} assignmentCount={assignmentCount} showFees={showFees} />

      <div className="tw-grid tw-gap-4 min-[1200px]:tw-grid-cols-3">
        <div className="tw-min-w-0 min-[1200px]:tw-col-span-2">
          <BatchFactsCard batch={data} />
        </div>
        <BatchTeamCard instructors={instructors} />
      </div>

      <Tabs value={tab} onChange={setTab}>
        <TabsList overflow>
          <TabsTrigger value="students"><Users size={15} className="tw-mr-1" aria-hidden="true" /> {terms.plural}</TabsTrigger>
          <TabsTrigger value="syllabus"><CalendarDays size={15} className="tw-mr-1" aria-hidden="true" /> Syllabus</TabsTrigger>
          <TabsTrigger value="videos"><Video size={15} className="tw-mr-1" aria-hidden="true" /> Videos</TabsTrigger>
          <TabsTrigger value="materials"><BookOpen size={15} className="tw-mr-1" aria-hidden="true" /> Materials</TabsTrigger>
          <TabsTrigger value="attendance"><UserCheck size={15} className="tw-mr-1" aria-hidden="true" /> Attendance</TabsTrigger>
          <TabsTrigger value="assignments"><ClipboardList size={15} className="tw-mr-1" aria-hidden="true" /> Assignments</TabsTrigger>
          <TabsTrigger value="requests">
            <UserPlus size={15} className="tw-mr-1" aria-hidden="true" /> Requests
            {pending > 0 && (
              <StatusBadge status="warning" className="tw-ml-1.5">
                {pending}<span className="tw-sr-only"> waiting</span>
              </StatusBadge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="students">
          <BatchStudentsTab
            batchId={id}
            showFees={showFees}
            canEnrol={can.enrol}
            canRemove={canRemove}
            batch={{ _id: id, name: data.name || "", courseName: data.course?.name || "" }}
            terms={terms}
            onNotify={notify}
            onRosterChange={refreshCounts}
          />
        </TabsContent>

        <TabsContent value="syllabus">
          <BatchSyllabusSchedule
            batchId={id}
            canReschedule={can.reschedule}
            canGenerate={can.runSyllabus}
            onPlanChange={setSyncedOverride}
            canEditBatch={can.editBatch}
            onNotify={notify}
          />
        </TabsContent>

        <TabsContent value="videos">
          <BatchVideosPage batchId={id} role={role} currentUserId={currentUserId} />
        </TabsContent>

        <TabsContent value="materials">
          <BatchMaterialsPage batchId={id} courseId={courseId} role={role} currentUserId={currentUserId} />
        </TabsContent>

        <TabsContent value="attendance">
          <BatchAttendanceTab batchId={id} registerOn={registerOn} />
        </TabsContent>

        <TabsContent value="assignments">
          <BatchGeneralAssignments batchId={id} />
        </TabsContent>

        <TabsContent value="requests">
          <BatchEnrollmentRequests batchId={id} onCountChange={setRequestCount} />
        </TabsContent>
      </Tabs>

      {can.editBatch && (
        <>
          <EndBatchModal
            show={endOpen}
            onHide={() => setEndOpen(false)}
            batchName={data.name}
            busy={busy === "end"}
            onConfirm={endBatch}
          />
          <ContentExpiryModal
            show={expiryOpen}
            onHide={() => setExpiryOpen(false)}
            batch={data}
            busy={busy === "expiry"}
            onSave={saveExpiry}
          />
        </>
      )}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default BatchDetailsPage;
